import { z } from 'zod'

import { ApiClientError } from '@ems/api-client/client'

import { mediaItemSchema } from '#admin/features/cms/api.ts'
import { api } from '#admin/lib/api-client.ts'

/**
 * The media library page: every file the edition has uploaded, with where each one is used.
 *
 * The rows are the picker's (`mediaItemSchema`, from `features/cms`) plus what only this page
 * shows — the size on disk and the usage — so a row seeded into `mediaByKeyQuery` for the crop
 * dialog is the same shape the dialog would have fetched.
 */

export const mediaUsageSchema = z.object({
  /** Every live document that points at the file, drafts included. */
  count: z.number().int(),
  /** The first few of them; `count` says how many more there are. */
  documents: z.array(
    z.object({ id: z.string(), collection: z.string(), slug: z.string(), locale: z.string() }),
  ),
})

export type MediaUsage = z.infer<typeof mediaUsageSchema>

export const libraryItemSchema = mediaItemSchema.extend({
  byteSize: z.number().int(),
  usage: mediaUsageSchema,
})

export type LibraryItem = z.infer<typeof libraryItemSchema>

const libraryPageSchema = z.object({
  items: z.array(libraryItemSchema),
  nextCursor: z.string().nullable(),
})

/** The page's filter. `file` is everything that is not an image — PDFs, mostly. */
export const MEDIA_KINDS = ['all', 'image', 'file'] as const

export type MediaKind = (typeof MEDIA_KINDS)[number]

export function isMediaKind(value: unknown): value is MediaKind {
  return typeof value === 'string' && (MEDIA_KINDS as readonly string[]).includes(value)
}

/**
 * One page of the library, newest first.
 *
 * The filter is the server's, not applied here, for the reason `fetchMediaLibrary` gives: the
 * server pages after filtering, so a client-side filter would hand back short pages.
 */
export function fetchLibraryPage(
  filter: { q: string; kind: MediaKind; cursor: string | undefined },
  signal?: AbortSignal,
): Promise<z.infer<typeof libraryPageSchema>> {
  const query = new URLSearchParams({ limit: '50' })
  if (filter.kind !== 'all') query.set('kind', filter.kind)
  if (filter.q !== '') query.set('q', filter.q)
  if (filter.cursor !== undefined) query.set('cursor', filter.cursor)

  return api.get(`/v1/cms/admin/media?${query.toString()}`, {
    schema: libraryPageSchema,
    ...(signal ? { signal } : {}),
  })
}

/**
 * Remove a file from the library. The object stays in storage; only the library row goes.
 *
 * Refused with a 409 while any document uses it — `usageFromRefusal` reads who.
 */
export function deleteMedia(id: string): Promise<{ deleted: boolean }> {
  return api.delete(`/v1/cms/admin/media/${id}`, {
    schema: z.object({ deleted: z.boolean() }),
  })
}

const inUseDetailSchema = z.object({
  code: z.literal('CMS_MEDIA_IN_USE'),
  usedBy: mediaUsageSchema.shape.documents,
  usedByCount: z.number().int().optional(),
})

/**
 * The documents named by a delete refused because the file is in use, or `null` for any other
 * failure.
 *
 * The page already shows the usage before offering a delete, so this is the race: somebody put
 * the file on a page between the list loading and the click.
 */
export function usageFromRefusal(error: unknown): MediaUsage | null {
  if (!(error instanceof ApiClientError) || error.status !== 409) return null
  const detail = inUseDetailSchema.safeParse(error.detail)
  if (!detail.success) return null
  return {
    count: detail.data.usedByCount ?? detail.data.usedBy.length,
    documents: detail.data.usedBy,
  }
}

/** `1234567` → `1.2 MB`. Decimal units, as the upload limits are stated. */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`
  return `${(bytes / 1_000_000).toFixed(1)} MB`
}
